<div class="chat-widget">

  <!-- Chat Header -->
  <div class="chat-widget__header">
    <span class="chat-widget__title">{{ 'chat.title' | t }}</span>
    <!-- Dev-build only: debtors restart via the terminal-state button -->
    @if (isDevMode()) {
    <div class="chat-widget__header-actions">
      <button
        class="chat-widget__new-session-btn"
        type="button"
        [disabled]="isLoading()"
        data-track="chat_restart" (click)="startNewSession()"
        [attr.aria-label]="'chat.newSessionAria' | t">
        {{ 'chat.newSession' | t }}
      </button>

      <!-- 3-Dot Menu -->
      <div class="chat-widget__menu-wrap">
        <button
          class="chat-widget__menu-btn"
          type="button"
          (click)="toggleMenu()"
          [attr.aria-label]="'chat.openMenu' | t"
          aria-haspopup="true"
          [attr.aria-expanded]="menuOpen()">
          ⋮
        </button>
        @if (menuOpen()) {
          <div class="chat-widget__menu-dropdown">
            <button
              class="chat-widget__menu-item"
              type="button"
              (click)="openSessionReportModal(); closeMenu()">
              {{ 'chat.reportSession' | t }}
            </button>
            <button
              class="chat-widget__menu-item"
              type="button"
              (click)="copySessionId(); closeMenu()">
              {{ sessionIdLabel() }}
            </button>
            <button
              class="chat-widget__menu-item"
              type="button"
              [disabled]="messages().length === 0"
              (click)="copyChat(); closeMenu()">
              {{ copyLabel() }}
            </button>
          </div>
        }
      </div>
    </div>
    }
  </div>

  <!-- Message List -->
  <div class="chat-widget__messages" #messageList (click)="closeMenu()">

    @if (messages().length === 0 && isLoading()) {
      <div class="chat-widget__loading-initial">
        <span class="chat-widget__dot"></span>
        <span class="chat-widget__dot"></span>
        <span class="chat-widget__dot"></span>
      </div>
    }

    @for (msg of messages(); track msg.timestamp; let msgIndex = $index) {
      <div class="chat-bubble" [class.chat-bubble--bot]="msg.type === 'bot'" [class.chat-bubble--user]="msg.type === 'user'">
        <div class="chat-bubble__text">@if (msg.type === 'bot' && privacyLink(msg); as link) {
          {{ link.before }}<a [href]="link.url" target="_blank" rel="noopener">{{ 'chat.privacyLink' | t }}</a>{{ link.after }}
        } @else { {{ msg.text }} }</div>
        <div class="chat-bubble__time">{{ msg.timestamp | date:'HH:mm' }}</div>

        @if (msg.type === 'bot' && msg.widgets && msg.widgets.length > 0) {
          <app-widget-card [widgets]="msg.widgets" [actions]="msg.actions ?? []"
            [disabled]="isLoading() || !isLatestBotMessage(msgIndex)"
            [claimDetailsAvailable]="claimDetails.observed" (claimDetails)="claimDetails.emit($event)"
            (actionClick)="onActionClick($event, msgIndex)"></app-widget-card>
        }

        @if (isDevMode() && msg.type === 'bot' && msg.debugExport) {
          <div class="chat-bubble__debug-export">
            <div class="chat-bubble__debug-export-copy">
              <strong>{{ 'p1.debugExport' | t }}</strong>
              <span>{{ msg.debugExport.warning }}</span>
              @if (msg.debugExport.fileName) {
                <small>{{ msg.debugExport.fileName }}</small>
              }
            </div>
            <a
              class="chat-bubble__action-btn"
              [href]="msg.debugExport.downloadUrl"
              target="_blank"
              rel="noopener">
              {{ msg.debugExport.label }}
            </a>
          </div>
        }

        @if (msg.type === 'bot' && isLatestBotMessage(msgIndex) && msg.actions && msg.actions.length > 0 && !msg.widgets?.[0]?.data?.['countOptions']) {
          <div class="chat-bubble__actions">
            @for (action of msg.actions; track $index) {
              <button
                class="chat-bubble__action-btn"
                data-track="chat_action" (click)="onActionClick(action, msgIndex)"
                type="button">
                {{ localizedActionLabel(action) }}
              </button>
            }
          </div>
        }
      </div>
    }

    @if (isTerminalState()) {
      <div class="chat-bubble__actions chat-widget__terminal-action">
        <button
          class="chat-bubble__action-btn"
          type="button"
          [disabled]="isLoading()"
          data-track="chat_restart" (click)="startNewSession()">
          {{ 'chat.newSessionAria' | t }}
        </button>
      </div>
    }

    <!-- Bot typing indicator -->
    @if (isLoading() && messages().length > 0) {
      <div class="chat-bubble chat-bubble--bot chat-bubble--typing">
        <span class="chat-widget__dot"></span>
        <span class="chat-widget__dot"></span>
        <span class="chat-widget__dot"></span>
      </div>
    }
  </div>

  <!-- Input Area -->
  <div class="chat-widget__input-area">
    <textarea
      class="chat-widget__input"
      #chatInput
      rows="1"
      [placeholder]="'chat.inputPlaceholder' | t"
      [value]="inputText()"
      (input)="onInputChange(chatInput.value)"
      (keydown)="onKeyDown($event)"
      [disabled]="isLoading()"
      [attr.aria-label]="'chat.inputAria' | t"
    ></textarea>
    <button
      class="chat-widget__send-btn"
      type="button"
      [disabled]="!canSend()"
      data-track="chat_send" (click)="send()"
      [attr.aria-label]="'chat.sendAria' | t">
      {{ 'common.send' | t }}
    </button>
  </div>

  @if (showInsolvencyModal()) {
    <app-insolvency-modal
      (close)="showInsolvencyModal.set(false)"
      (submitInsolvency)="submitInsolvency($event)" />
  }

  <!-- Stammdaten change modal overlay -->
  @if (showStammdatenModal()) {
    <app-stammdaten-modal
      [currentData]="stammdatenCurrentData()"
      (close)="closeStammdatenModal()"
      (submitStammdaten)="submitStammdatenChange($event)" />
  }

  <!-- Contact form modal overlay -->
  @if (showContactModal()) {
    <app-contact-form-modal
      [phoneRequired]="contactPhoneRequired()"
      (close)="closeContactModal()"
      (submitContact)="closeContactModal($event)" />
  }

  @if (showDocumentUploadModal()) {
    <app-document-upload-modal
      [modalNonce]="documentUploadNonce()"
      (close)="skipDocumentUpload()"
      (uploadComplete)="closeDocumentUploadModal($event)" />
  }

  @if (showDeleteFormTodoModal()) {
    <app-delete-form-todo-modal
      (close)="closeDeleteFormTodoModal()"
      (submitTodoForm)="closeDeleteFormTodoModal(true)" />
  }

	  @if (showSepaModal()) {
	    <app-sepa-modal
          [accountHolder]="modalData()['kontoinhaber'] || ''"
	      [defaultDebitDay]="modalData()['abbuchungstag'] || modalData()['rate_day'] || ''"
	      (close)="closeSepaModal()"
	      (submitMandat)="submitSepaMandat($event)" />
	  }

  @if (showSessionReportModal()) {
    <div class="modal-backdrop chat-widget__report-backdrop" role="presentation">
      <div
        class="modal-panel chat-widget__report-dialog"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="'chat.reportSessionTitle' | t">
        <h2 class="chat-widget__report-title">{{ 'chat.reportSessionTitle' | t }}</h2>
        <p class="chat-widget__report-text">{{ 'chat.reportSessionHelp' | t }}</p>

        <textarea
          class="chat-widget__report-input"
          #sessionReportInput
          rows="6"
          [value]="sessionReportText()"
          [disabled]="sessionReportSending() || sessionReportStatus() === 'success'"
          [attr.placeholder]="'chat.reportSessionPlaceholder' | t"
          [attr.aria-label]="'chat.reportSessionTextareaAria' | t"
          (input)="onSessionReportTextChange(sessionReportInput.value)"></textarea>

        <label class="chat-widget__report-text" for="session-report-tester-name">
          {{ 'chat.reportSessionTesterNameLabel' | t }}
        </label>
        <input
          class="chat-widget__input"
          id="session-report-tester-name"
          #sessionReportTesterNameInput
          type="text"
          [value]="sessionReportTesterName()"
          [disabled]="sessionReportSending() || sessionReportStatus() === 'success'"
          [attr.aria-label]="'chat.reportSessionTesterNameAria' | t"
          (input)="onSessionReportTesterNameChange(sessionReportTesterNameInput.value)" />

        @if (sessionReportStatus() === 'success') {
          <div class="chat-widget__report-feedback chat-widget__report-feedback--success">
            {{ 'chat.reportSessionSuccess' | t }}
          </div>
        }

        @if (sessionReportStatus() === 'error') {
          <div class="chat-widget__report-feedback chat-widget__report-feedback--error">
            {{ 'chat.reportSessionError' | t }}
          </div>
        }

        <div class="chat-widget__report-actions">
          <button
            class="btn-cancel chat-widget__report-secondary"
            type="button"
            [disabled]="sessionReportSending()"
            (click)="closeSessionReportModal()">
            {{ sessionReportSecondaryLabel() }}
          </button>
          @if (sessionReportStatus() !== 'success') {
            <button
              class="btn-submit chat-widget__report-primary"
              type="button"
              [disabled]="!canSubmitSessionReport()"
              (click)="submitSessionReport()">
              {{ sessionReportSending() ? ('chat.reportSessionSending' | t) : ('chat.reportSessionSubmit' | t) }}
            </button>
          }
        </div>
      </div>
    </div>
  }

  @if (modalSubmitError()) {
    <div
      class="chat-widget__report-feedback chat-widget__report-feedback--error"
      role="alert"
      style="position: fixed; left: 50%; bottom: max(24px, env(safe-area-inset-bottom)); z-index: 1001; width: min(520px, calc(100vw - 32px)); transform: translateX(-50%);">
      {{ modalSubmitError() }}
    </div>
  }

</div>
